// src/services/messages.js — send (seal per-device) + decrypt pipeline
import { applyMediaMeta, metaPending, openMediaMeta } from '../crypto/mediaMeta';   // 🔐 a sealed caption + thumbnails
import {
  collection, doc, setDoc, updateDoc, serverTimestamp, deleteField, increment,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { sealForUsers, decryptPerDeviceText, unsealMediaKey } from '../crypto/e2ee';
import { decryptMediaUrl, guessMime, cachedMediaUrl } from '../crypto/media';
import { relayNewMessage } from './socket';
import { addMessageDoc } from './sendDoc'; // 🖥📱 origin + my other devices' copy

// ── SEND: 1:1 text, sealed to every device of both parties (mobile-compatible) ──
/* 🟢 `tempId` LETS A CALLER OWN THE ID BEFORE THE MESSAGE EXISTS. The guest
   outbox draws its optimistic bubble from a queued job, and the only way that
   bubble can be retired when the real document lands is for the two to share a
   clientTempId. Callers that don't care still get a generated one. */
/**
 * @param extraFields plaintext metadata to ride along on the message document.
 *
 * 🟥 METADATA, NEVER CONTENT. Anything passed here is written in the clear and
 *    relayed in the clear, so it is for flags the renderer needs before it has
 *    unsealed anything — `isAI` is one, the same way `linkMetadata` above is.
 *    A caller that puts words in here has just written an unencrypted message.
 */
export async function sendTextMessage({ chatId, participants, senderId, text, isGroup, reply, linkMetadata, tempId, extraFields, mentions, mentionsEveryone }) {
  const clean = (text || '').trim();
  if (!clean || !chatId) return;
  const { senderDeviceId, perDeviceKeys } = await sealForUsers(clean, participants, { preview: true }); // 🔒 long-text preview

  const clientTempId = tempId || `web_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const docData = {
    type: 'text',
    senderId,
    timestamp: serverTimestamp(),
    sent: true,
    delivered: false,
    seen: false,
    // ciphertext lives in perDeviceKeys; the plaintext field is a placeholder so
    // any non-E2EE reader shows the lock, never the content.
    text: '🔒 Encrypted message',
    senderDeviceId,
    perDeviceKeys,
    clientTempId,
    // 🟥 PLAINTEXT, next to the ciphertext, on purpose. The app reads
    //    message.linkMetadata directly with no unseal step — seal it and its card
    //    renders as nothing. It describes a public page, which is why both clients
    //    (and the server's relay) treat it as safe to move in the clear.
    ...(linkMetadata ? { linkMetadata } : {}),
    /* 🟥 UIDS, AND NOT ONE NAME — the same contract the app writes. Every client
       re-derives the blue from the group's CURRENT member list, which is what makes
       a mention follow somebody who renames themselves, and what keeps a display
       name off a document the server is not supposed to be able to read. Written
       only when somebody was actually named. */
    ...(Array.isArray(mentions) && mentions.length ? { mentions } : {}),
    /* 🟥 A FLAG, NOT A UID. `mentions` stays a list of real people so nothing
       downstream tries to look up a name or open a listener for the room. */
    ...(mentionsEveryone ? { mentionsEveryone: true } : {}),
    ...(extraFields || {}),
    // 🟢 the sealed quote, spread verbatim into BOTH the Firestore doc and the socket
    //    payload. The server treats replyTo as an opaque blob and re-stringifies it, so
    //    an object here is exactly right; the two *PerDeviceKeys maps must stay objects.
    ...(reply || {}),
  };

  const ref = await addMessageDoc(collection(db, 'chats', chatId, 'messages'), docData);

  // 🟢 RELAY — this is what makes the message actually ARRIVE: the server's
  //    `new-message` handler does the fast socket relay to the recipient, advances
  //    the delivered watermark, assigns the seq, and SENDS THE FCM PUSH. Writing to
  //    Firestore alone (what we did before) notified nobody, so the recipient only
  //    saw it whenever they next opened the app.
  try {
    relayNewMessage({
      chatId, messageId: ref.id, fromUserId: senderId, participants,
      isGroup: !!isGroup, cloudText: '🔒 Encrypted message',
      perDeviceKeys, senderDeviceId, clientMsgId: clientTempId,
      mentions: Array.isArray(mentions) ? mentions : [],
      mentionsEveryone: !!mentionsEveryone,
      extra: {
        ...(reply || {}),
        // a JSON STRING on new-message, an object on link-preview-update — that
        // asymmetry is the app's, and its parsers depend on it
        ...(linkMetadata ? { linkMetadata: JSON.stringify(linkMetadata) } : {}),
        /* 🟢 ON THE RELAY TOO, or the recipient's phone draws an ordinary bubble
           for the second or two before the Firestore snapshot arrives and then
           grows a badge. The socket payload is what actually reaches them. */
        ...(extraFields || {}),
      },
    });
  } catch (e) { console.warn('[send] relay failed:', e?.message); }

  // 🟥 MIRROR THE PHONE'S CHAT-DOC WRITE **EXACTLY** (ChatModal sendMessage ~19011).
  //    The old write used `lastMessageTime` — a field NOTHING on the phone reads.
  //    ChatsTab sorts AND displays by `timestamp`, and derives the row's checkmarks
  //    from lastMessageId + lastMessageSent/Delivered/Seen. Omitting them meant the
  //    doc still described the user's PREVIOUS app-sent message: its time kept
  //    winning the row ("the app message wins the fight") and its ticks never left.
  //    unreadCounts increments were missing too — the recipient's badge never bumped
  //    for web sends. lastMessageSystem is cleared like the phone does, so a web text
  //    also retires a stale "reacted…" preview.
  const _unread = {};
  (participants || []).forEach((uid) => {
    if (!uid) return;
    _unread[uid] = uid === senderId ? 0 : increment(1);
  });
  await setDoc(
    doc(db, 'chats', chatId),
    {
      lastMessage: '🔒 Encrypted message',
      timestamp: serverTimestamp(),
      lastMessageSystem: deleteField(),
      lastMessageSenderId: senderId,
      lastMessageSent: true,
      lastMessageDelivered: false,
      lastMessageSeen: false,
      lastMessageId: ref.id,
      unreadCounts: _unread,
    },
    { merge: true }
  ).catch(() => {});

  // 🟢 both ids come back: the doc id for a Firestore patch, and the clientTempId that
  //    the link-preview backfill is keyed on — in a 1:1 chat the doc is often gone by
  //    the time a preview resolves, and the temp id is the only handle left
  return { id: ref.id, clientTempId };
}

/** Decrypt ONE item of an album on demand (the "Load video" tap inside a batch).
 *  Handles both shapes: a live Firestore doc (sealed batchPerDeviceKeys) and a
 *  phone-synced history entry (plaintext mediaKeys array). */
export async function loadBatchItem(msg, index) {
  try {
    const url = msg?.images?.[index];
    if (!url) return null;
    let mk = Array.isArray(msg.mediaKeys) ? msg.mediaKeys[index] : null;
    if (!mk && msg.batchPerDeviceKeys) {
      const arr = await unsealMediaKey(msg.batchPerDeviceKeys, msg.senderId, msg.senderDeviceId);
      mk = Array.isArray(arr) ? arr[index] : null;
    }
    if (!mk?.k || !mk?.n) return null;
    const kind = mk.type === 'video' || /\.(mp4|mov)/i.test(url) ? 'video' : 'image';
    return await decryptMediaUrl(url, mk.k, mk.n, guessMime(url, kind));
  } catch {
    return null;
  }
}

export async function markMessageSeen(chatId, messageId) {
  try {
    await updateDoc(doc(db, 'chats', chatId, 'messages', messageId), {
      seen: true, delivered: true, seenAt: serverTimestamp(),
    });
  } catch {}
}

// ── DECRYPT: normalize any incoming message into a render-ready shape ──
// Returns { ...msg, _plain, _media } where _media is a resolved object URL when applicable.
export async function decryptMessage(msg, myUid, opts = {}) {
  const out = { ...msg };
  // 🟢 opts.skipVideo — videos are the expensive ones (tens of MB through fetch +
  //    secretbox before a single frame shows). The chat asks for them to be SKIPPED on
  //    autoload and fetches them only when the user taps "Load video".

  // text (per-device) — self messages we sent from THIS browser we already know, but
  // decrypting still works because we sealed to our own device too.
  // 🟢 'security' is the phone's "a new device signed in" card. Its payload is JSON
  //    sealed in the text field precisely so it rides the ordinary text decrypt — no new
  //    crypto, no new failure mode. SecurityCard parses the JSON back out.
  /* 🟥 'support' RIDES THE TEXT PATH, AND MUST. Messages from the official Allo
     account are the one kind in the product that is NOT encrypted — somebody on
     the Allo team has to read a ticket to answer it — so they arrive with a
     plain `text` field and no `perDeviceKeys`. Left out of this branch they got
     no `_plain` at all, fell through MessageBody's switch, and rendered as
     "Unsupported message": every answer Allo gave was invisible. */
  /* 🔐 A SEALED CAPTION + THUMBNAILS (../crypto/mediaMeta): opened onto the row under the media's
     own key before anything below reads `caption` or a thumbnail */
  if (metaPending(msg) && (msg.type === 'image' || msg.type === 'video' || msg.type === 'images')) {
    try {
      const keys = msg.type === 'images' ? msg.batchPerDeviceKeys : (msg.type === 'video' ? msg.videoPerDeviceKeys : msg.imagePerDeviceKeys);
      const mk = keys ? await unsealMediaKey(keys, msg.senderId, msg.senderDeviceId) : null;
      const k = Array.isArray(mk) ? mk[0]?.k : mk?.k;
      if (k) applyMediaMeta(out, openMediaMeta(k, msg.metaCipher, msg.metaNonce));
    } catch { /* drawn without them, as an older build's row would be */ }
  }
  if (msg.type === 'text' || msg.type === 'security' || msg.type === 'support') {
    out._plain = (await decryptPerDeviceText(msg)) ?? (msg.senderId === myUid ? null : null);
    // 🟥 NO perDeviceKeys MEANS THE TEXT IS THE TEXT. This used to require
    //    `msg.forwarded`, which is one narrow case of a general rule: the lock
    //    placeholder is only ever written ALONGSIDE a per-device map, so a message with
    //    no map and a real text field is plaintext — a history export, a legacy
    //    unsealed row, a forward. Treating those as unreadable printed "Sent before this
    //    device was linked" over content we were holding in our hand.
    if (out._plain == null && !msg.perDeviceKeys && msg.text && !String(msg.text).startsWith('🔒')) {
      out._plain = msg.text;
    }
    return out;
  }

  // single image / video / audio / document — one ciphertext URL + one sealed key
  /* 🟥 `mime` IS THE SENDER'S ANSWER, AND IT BEATS A GUESS FROM THE URL. The R2
     object key carries no extension, so guessMime() can only fall back on `kind`
     — and for audio that fallback is a flat 'audio/mp4'. A browser recording is
     usually WebM/Opus, so the decrypted Blob got labelled as something it is not:
     the element sniffed the container and PLAYED IT ANYWAY, while its own
     controls sat there showing a load error. Audible and broken-looking at the
     same time, which is a maddening thing to debug.

     `audioMime` / `documentType` are written by the senders that know; anything
     older simply has no hint and keeps the old behaviour. */
  /* 🟢 A LAST-RESORT `url`, AND ONLY THAT. The phone's export calls this field
     `url` where a Firestore document calls it `imageUrl`; that shape belongs to
     normalizeSynced and isExportedEntry sends it there. This is the safety net
     for the one thing that cannot be checked from here — an export written by a
     phone build old enough not to carry the marker. Rendering a url we are
     holding beats rendering "Media unavailable" over it, which is precisely the
     failure this exists to stop. */
  const own = (u) => u || (typeof msg.url === 'string' ? msg.url : undefined);
  const single = {
    image: { url: own(msg.imageUrl), keys: msg.imagePerDeviceKeys, kind: 'image', mime: msg.imageMime },
    video: { url: own(msg.videoUrl), keys: msg.videoPerDeviceKeys, kind: 'video', mime: msg.videoMime },
    audio: { url: own(msg.audioUrl), keys: msg.audioPerDeviceKeys, kind: 'audio', mime: msg.audioMime },
    document: {
      url: own(msg.documentUrl), keys: msg.documentPerDeviceKeys, kind: 'document',
      mime: msg.documentType,
    },
  }[msg.type];
  if (single) {
    // 🟥 A FORWARDED MEDIA MESSAGE'S `perDeviceKeys` IS NOT ITS CAPTION. The phone's
    //    forward path seals the per-type PREVIEW ("📷 Photo") into that field so the
    //    push notification and chat list can read something — the real caption, if the
    //    original had one, is copied to the plaintext `caption` field beside it. Reading
    //    the seal here printed a literal "📷 Photo" underneath every forwarded photo.
    out._caption = msg.forwarded
      ? (out.caption || '')
      : ((await decryptPerDeviceText({ ...msg, perDeviceKeys: msg.perDeviceKeys })) || out.caption || '');
    if (opts.skipVideo && msg.type === 'video' && single.url) {
      /* 🟥 THE BUTTON ASKS ABOUT A DOWNLOAD, AND WAS ASKED ABOUT BYTES ALREADY
         IN HAND. `_media` is state and state does not survive leaving a chat,
         so a clip watched an hour ago asked to be loaded again every time. The
         decrypted bytes are on disk now, so the cost the button guards no
         longer exists for them. */
      if (single.keys) {
        const mk = await unsealMediaKey(single.keys, msg.senderId, msg.senderDeviceId);
        const have = mk?.k ? await cachedMediaUrl(single.url, mk.k) : null;
        if (have) { out._media = have; return out; }
        out._needsLoad = true; // poster only; the bubble shows a Load button
        return out;
      }
      /* 🟢 AND AN UNSEALED CLIP NEVER NEEDED IT. With no key there is nothing to
         fetch and nothing to decrypt — the url IS the source, and
         `preload="metadata"` takes the header and stops. The button was
         guarding a cost that only exists for sealed media, and it is why the
         official account's videos asked to be loaded on every single visit. */
      if (msg.e2eeMedia === true) { out._media = null; return out; }   // 🔒 keys lost ≠ plaintext
      out._media = single.url;
      return out;
    }
    if (single.url && single.keys) {
      const mk = await unsealMediaKey(single.keys, msg.senderId, msg.senderDeviceId);
      if (mk?.k && mk?.n) {
        out._media = await decryptMediaUrl(
          single.url, mk.k, mk.n, single.mime || guessMime(single.url, single.kind),
        );
      }
    } else if (single.url && !(msg.e2eeMedia === true || msg.e2eeImage === true)) {
      out._media = single.url; // unencrypted (legacy/self) — 🔒 never a copy that is MARKED encrypted and lost its keys
    }
    // 🟢 A DOCUMENT'S PREVIEW IS ITS OWN ENCRYPTED UPLOAD — separate URL, separate
    //    symmetric key, and note the sender-device id lives in its OWN field
    //    (`documentThumbSenderDeviceId`), not the shared `senderDeviceId`. Using the
    //    shared one unseals against the wrong device key and silently yields nothing.
    if (msg.type === 'document' && msg.documentThumbUrl && msg.documentThumbPerDeviceKeys) {
      try {
        const tk = await unsealMediaKey(
          msg.documentThumbPerDeviceKeys, msg.senderId,
          msg.documentThumbSenderDeviceId || msg.senderDeviceId,
        );
        if (tk?.k && tk?.n) {
          out._docThumb = await decryptMediaUrl(msg.documentThumbUrl, tk.k, tk.n, 'image/png');
        }
      } catch { /* the icon card is a fine document bubble */ }
    } else if (msg.type === 'document' && msg.documentThumbUrl) {
      /* 🟥 A PLAINTEXT FIRST PAGE, which had no branch at all. The condition
         above requires `documentThumbPerDeviceKeys`, so a thumbnail url with no
         keys was skipped in silence — the card reserved the preview strip, found
         nothing, and collapsed. That is exactly the reported difference: a
         document sent from a phone showed its first page, the same document sent
         from the admin portal did not.

         Same rule as every other media field here: keys present means decrypt,
         keys absent means the url IS the image. An official broadcast cannot seal
         a thumbnail — there is no key thousands of recipients could share. */
      out._docThumb = msg.documentThumbUrl;
    }
    return out;
  }

  // batch (images/videos) — array of ciphertext URLs + array of sealed keys
  if (msg.type === 'images' && Array.isArray(msg.images)) {
    out._caption = msg.forwarded ? (out.caption || '') : ((await decryptPerDeviceText({ ...msg, perDeviceKeys: msg.perDeviceKeys })) || out.caption || '');
    let keyArr = null;
    if (msg.batchPerDeviceKeys) keyArr = await unsealMediaKey(msg.batchPerDeviceKeys, msg.senderId, msg.senderDeviceId);
    /* 🟥 AN ALBUM FROM THE OFFICIAL ACCOUNT CAME OUT EMPTY, and the reason is one
       word in the old comment below: "http+no key = ciphertext". That is true of
       an album whose keys we FAILED to unseal, and false of one that never had
       any. A broadcast of several photos is written with no `batchPerDeviceKeys`
       at all — deliberately, because the absence of keys is what marks these urls
       plaintext, the same rule the single image, the video and the document card
       ten lines up all follow. Every tile was therefore thrown away as unreadable
       ciphertext, and the browser drew a card of spinners that never resolved.
       🟢 SO THE QUESTION IS ASKED OF THE MESSAGE, NOT OF THE ITEM. A sealed batch
          whose unseal failed still skips — that item genuinely is ciphertext we
          cannot read, and rendering it would put broken bytes in an <img>. */
    const sealedBatch = !!(msg.batchPerDeviceKeys || msg.batchEncryptionKey || msg.groupBatchEncryptionData || msg.e2eeMedia === true);   // 🔒 a copy that lost its keys is still ciphertext
    out._mediaBatch = await Promise.all(
      msg.images.map(async (url, i) => {
        const mk = Array.isArray(keyArr) ? keyArr[i] : null;
        const kind = mk?.type === 'video' || /\.(mp4|mov)/i.test(url) ? 'video' : 'image';
        // a video INSIDE an album is skipped the same way a single one is
        if (opts.skipVideo && kind === 'video') return { kind, url: null, needsLoad: true };
        if (url && mk?.k && mk?.n) {
          /* 🟥 ONE TILE MUST NOT TAKE THE ALBUM. This is a Promise.all: a single
             rejection leaves `_mediaBatch` unset, and BatchBody then falls back to
             images.length spinners — every tile "loading" while the first still
             draws its inline blur, which is what got reported as "the first is
             broken and the rest are just loading". A tile that cannot be read is
             one empty tile, not a blank card. */
          try {
            return { kind, url: await decryptMediaUrl(url, mk.k, mk.n, guessMime(url, kind)) };
          } catch (e) {
            console.warn(`[media] album tile ${i} failed (${e?.name || 'error'}) — the other tiles are unaffected`, url, e);
            return { kind, url: null };
          }
        }
        return { kind, url: /^https?:/.test(url) && sealedBatch && !mk ? null : url };
      })
    );
    return out;
  }

  // sticker / gif — usually a plain remote URL (Giphy) or a sealed sticker
  if (msg.type === 'sticker' || msg.type === 'gif') {
    out._media = msg.stickerUrl || msg.gifUrl || msg.imageUrl || null;
    return out;
  }

  // location / contact / poll — decrypt the JSON payload for renderers
  const jsonSealed = {
    location: msg.locationPerDeviceKeys,
    contact: msg.contactPerDeviceKeys,
    poll: msg.pollPerDeviceKeys,
  }[msg.type];
  if (jsonSealed) {
    const json = await unsealMediaKey(jsonSealed, msg.senderId, msg.senderDeviceId);
    out._payload = json || null;
    return out;
  }

  return out;
}
